import React from 'react';
import tsDate from '@techsee/techsee-date';
import {ColumnDef} from '@tanstack/react-table';
import {IDataTableColumn, DataTableColumnType} from './ts-data-table.contracts';

export function formatDate(dateStr: string): string {
    if (!dateStr) {
        return '—';
    }

    const m = tsDate(dateStr);

    return m.isValid() ? m.format('DD MMM YYYY, h:mm A') : '—';
}

function formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) {
        return '—';
    }

    const duration = tsDate.duration(seconds, 'seconds');
    const mins = Math.floor(duration.asMinutes());
    const secs = duration.seconds();

    return mins === 0 ? `${secs} sec` : `${mins} min ${secs} sec`;
}

const expanderCell = ({row}: {row: any}) => (
    <button
        className='expand-btn'
        onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
        }}
        title={row.getIsExpanded() ? 'Collapse' : 'Expand'}>
        {row.getIsExpanded() ? '-' : '+'}
    </button>
);

function getDefaultCell<TData>(
    type?: DataTableColumnType
): ((props: {getValue: () => any; row: any}) => any) | undefined {
    switch (type) {
        case DataTableColumnType.DateTime:
            return ({getValue}) => formatDate(getValue() as string);
        case DataTableColumnType.Duration:
            return ({getValue}) => formatDuration(getValue() as number);
        case DataTableColumnType.ExpandRow:
            return expanderCell;
        case DataTableColumnType.String:
        default:
            return undefined;
    }
}

export function resolveColumns<TData>(
    columns: IDataTableColumn<TData>[],
    isExpandable: boolean
): ColumnDef<TData, any>[] {
    const resolved: ColumnDef<TData, any>[] = [];

    const hasExpandColumn = columns.some((col) => col.type === DataTableColumnType.ExpandRow);

    if (isExpandable && !hasExpandColumn) {
        resolved.push({
            id: 'expander',
            header: () => null,
            cell: expanderCell,
            size: 25,
            enableSorting: false,
            meta: {type: DataTableColumnType.ExpandRow}
        });
    }

    for (const col of columns) {
        const columnDef: ColumnDef<TData, any> = {
            accessorKey: col.accessorKey,
            header: col.header || col.accessorKey,
            enableSorting: col.enableSorting !== false,
            meta: col.type ? {type: col.type} : undefined
        };

        const customCell = col.cell;

        if (customCell) {
            columnDef.cell = (ctx) => customCell({getValue: ctx.getValue, row: {value: ctx.row.original}});
        } else {
            const defaultCell = getDefaultCell<TData>(col.type);

            if (defaultCell) {
                columnDef.cell = defaultCell;
            }
        }

        if (col.size) {
            columnDef.size = col.size;
        }

        resolved.push(columnDef);
    }

    return resolved;
}
